import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, NgZone, Output, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

// Composants
import { CountrySelectComponent } from 'src/app/@shared/forms/components/selects/country-select/country-select.component';
import { FormBaseComponent } from '../../abstract/form-base.component';

// Modèles
import { Address } from 'src/app/@shared/models/address/address.model';
import { Civility } from 'src/app/@shared/models/user/civility.model';
import { Country } from 'src/app/@shared/models/core/country.model';

// Services
import { GoogleMapsLoaderService } from 'src/app/@core/services/google-maps/google-maps-loader.service';

// Utils
import { Sorter } from 'src/app/@shared/utilities/models/sorter';
import { log } from 'fabric/fabric-impl';

@Component({
    selector: 'app-address-form',
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        SelectModule,
        InputTextModule,
        CountrySelectComponent,
    ],
    templateUrl: './address-form.component.html',
    styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent extends FormBaseComponent implements AfterViewInit {

    /*----------------------------------------------------------------------------------------
    * Gestion de la vue
    ----------------------------------------------------------------------------------------*/

    public get isOrganization(): boolean {
        return this.address?.format.code === 'organization';
    }

    @Input()
    public showCancel: boolean = false;

    @Input()
    public hideSave: boolean = false;

    @Output()
    public addressChange: EventEmitter<Address> = new EventEmitter<Address>();
    @Output()
    public canceled: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public saved: EventEmitter<Address> = new EventEmitter<Address>();

    @ViewChild('placesContainer')
    private _placesContainerRef!: ElementRef<HTMLDivElement>;

    private readonly _ngZone = inject(NgZone);
    private readonly _googleMapsLoader = inject(GoogleMapsLoaderService);

    /*----------------------------------------------------------------------------------------
    * Données
    ----------------------------------------------------------------------------------------*/

    private _address!: Address;
    @Input()
    public get address(): Address {
        return this._address;
    }
    public set address(address: Address) {
        this._address = address;
        if (!!address) {
            this._mapToForm();
        } else {
            this.clearForm();
        }
    }

    private _civilities: Civility[] = [];
    public get civilities(): Civility[] {
        return this._civilities;
    }
    public set civilities(civilities: Civility[]) {
        let emptyArray: Civility[] = [];
        this._civilities = emptyArray.concat(civilities);
    }

    private _countries: Country[] = [];
    public get countries(): Country[] {
        return this._countries;
    }
    public set countries(countries: Country[]) {
        this._countries = ([] as Country[]).concat(countries);
    }

    /*----------------------------------------------------------------------------------------
    * Méthodes publiques
    ----------------------------------------------------------------------------------------*/

    public onClickCancel(): void {
        this.canceled.emit();
    }

    public override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this._googleMapsLoader.load().then(() => {
            this._initPlacesAutocomplete();
        }).catch(err => {
            console.warn('Google Maps Places non disponible :', err);
        });
    }

    /*----------------------------------------------------------------------------------------
    * Méthodes protégées
    ----------------------------------------------------------------------------------------*/

    protected override buildForm(): void {
        this.form = this.formBuilder.group({
            civility: null,
            firstName: '',
            lastName: '',
            secondCivility: null,
            secondFirstName: '',
            secondLastName: '',
            company: '',
            service: '',
            cedexCode: '',
            cedexLabel: '',
            appartment: '',
            building: '',
            street1: '',
            street2: '',
            zipCode: '',
            cityName: '',
            stateProvince: '',
            country: null,
            placeId: null
        });
    }

    protected override loadData(): void {
        this._loadCivilities();
        this._loadCountries();
    }

    protected override subscribeToObservables(): void {
        super.subscribeToObservables();
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                if (!!this.address) {
                    this._mapFromForm();
                    this.addressChange.emit(this.address);
                }
            })
        );
    }

    protected override save(): void {
        this._mapFromForm();
        this.loading = true;
        this.addressService.save$(this.address).pipe(
            finalize(() => this.loading = false)
        ).subscribe(address => {
            this.address = address;
            this.saved.emit(address);
        });
        this.form.markAsPristine();
    }

    /*----------------------------------------------------------------------------------------
    * Méthodes privées
    ----------------------------------------------------------------------------------------*/

    private _clearGeographicFields(): void {
        this.form.patchValue({
            cedexCode: '',
            cedexLabel: '',
            appartment: '',
            building: '',
            street1: '',
            street2: '',
            zipCode: '',
            cityName: '',
            stateProvince: '',
            country: null,
            placeId: null
        });
    }

    private _initPlacesAutocomplete(): void {
        if (!this._placesContainerRef?.nativeElement) return;

        const placeAutocomplete: any = new (google.maps.places as any).PlaceAutocompleteElement();
        placeAutocomplete.style.width = '100%';
        placeAutocomplete.style.colorScheme = 'light';
        placeAutocomplete.style.border = '1px solid rgb(203, 213, 225)';
        placeAutocomplete.style.backgroundColor = '#f9fafb';
        placeAutocomplete.style.borderRadius = '6px';
        placeAutocomplete.style.fontSize = '12px';
        placeAutocomplete.style.color = 'rgb(51, 65, 85)';
        placeAutocomplete.placeholder = 'Rechercher une adresse';
        this._placesContainerRef.nativeElement.replaceChildren(placeAutocomplete);

        placeAutocomplete.addEventListener('gmp-select', (event: any) => {
            this._ngZone.run(async () => {
                const place = event.placePrediction.toPlace();
                await place.fetchFields({
                    fields: ['addressComponents', 'displayName', 'types', 'id']
                });
                this._patchFormFromPlace(place);
            });
        });
    }

    private _patchFormFromPlace(place: any): void {
        const components: any[] = place.addressComponents ?? [];
        const getLong = (type: string): string =>
            components.find(c => c.types.includes(type))?.longText ?? '';
        const getShort = (type: string): string =>
            components.find(c => c.types.includes(type))?.shortText ?? '';

        const streetNumber = getLong('street_number');
        const route = getLong('route');
        const countryAlpha2 = getShort('country');
        const matchedCountry = this._countries.find(c => c.alpha2 === countryAlpha2) ?? null;

        const types: string[] = place.types ?? [];
        const isPoi = types.some(t => t === 'establishment' || t === 'point_of_interest');

        this._clearGeographicFields();

        this.form.patchValue({
            street1: [streetNumber, route].filter(Boolean).join(' '),
            zipCode: getLong('postal_code'),
            cityName: getLong('locality') || getLong('postal_town'),
            stateProvince: getLong('administrative_area_level_1'),
            country: matchedCountry,
            ...(isPoi && place.displayName ? { company: place.displayName } : {}),
            placeId: place.id ?? null
        });
    }

    private _loadCivilities(): void {
        const sorter = new Sorter({ property: 'label' });
        this.civilityService.getList$({ sorters: [sorter] })
            .subscribe(civilities => this.civilities = civilities);
    }

    private _loadCountries(): void {
        const sorter = new Sorter({ property: 'name' });
        this.countryService.getList$({ sorters: [sorter] })
            .subscribe(countries => this.countries = countries);
    }

    private _mapFromForm(): void {
        this.address.civility = this.form.get('civility')!.value;
        this.address.firstName = this.form.get('firstName')!.value;
        this.address.lastName = this.form.get('lastName')!.value;
        this.address.secondCivility = this.form.get('secondCivility')!.value;
        this.address.secondFirstName = this.form.get('secondFirstName')!.value;
        this.address.secondLastName = this.form.get('secondLastName')!.value;
        this.address.company = this.form.get('company')!.value;
        this.address.service = this.form.get('service')!.value;
        this.address.cedexCode = this.form.get('cedexCode')!.value;
        this.address.cedexLabel = this.form.get('cedexLabel')!.value;
        this.address.appartment = this.form.get('appartment')!.value;
        this.address.building = this.form.get('building')!.value;
        this.address.street1 = this.form.get('street1')!.value;
        this.address.street2 = this.form.get('street2')!.value;
        this.address.zipCode = this.form.get('zipCode')!.value;
        this.address.cityName = this.form.get('cityName')!.value;
        this.address.stateProvince = this.form.get('stateProvince')!.value;
        this.address.country = this.form.get('country')!.value;
        this.address.placeId = this.form.get('placeId')!.value;
    }

    private _mapToForm(): void {
        this.form.setValue({
            civility: this.address.civility ?? null,
            firstName: this.address.firstName ?? '',
            lastName: this.address.lastName ?? '',
            secondCivility: this.address.secondCivility ?? null,
            secondFirstName: this.address.secondFirstName ?? '',
            secondLastName: this.address.secondLastName ?? '',
            company: this.address.company ?? '',
            service: this.address.service ?? '',
            cedexCode: this.address.cedexCode ?? '',
            cedexLabel: this.address.cedexLabel ?? '',
            appartment: this.address.appartment ?? '',
            building: this.address.building ?? '',
            street1: this.address.street1 ?? '',
            street2: this.address.street2 ?? '',
            zipCode: this.address.zipCode ?? '',
            cityName: this.address.cityName ?? '',
            stateProvince: this.address.stateProvince ?? '',
            country: this.address.country ?? null,
            placeId: this.address.placeId ?? null
        });
    }

}
